import { Box, Center, Loader, Pagination, Table, Text } from '@mantine/core';
import dayjs from 'dayjs';
import { useState } from 'react';

import { useGetProphetPageAudit } from '../../api/getPageAudit';

import { useGetFunnels } from '@/features/pages';

const PAGE_SIZE = 20;

const FIELD_LABELS: Record<string, string> = {
  'config.isEnabled': 'Страница включена',
  'config.isKeywordsEnabled': 'Кодовые слова',
  'config.isRefusesEnabled': 'Возражения ИИ',
  'config.aiId': 'AI ID',
  'config.isNightModeEnabled': 'Ночной режим',
  'config.limits.perMinute': 'Сообщений в минуту',
  'config.limits.perHour': 'Сообщений в час',
  'config.limits.perDay': 'Сообщений в сутки',
  funnels: 'Воронки',
  'funnels.manager': 'Воронка менеджера',
  'funnels.completedNewDialog': 'Воронка завершённых диалогов',
  'funnels.completedNewDialogWithRefuse': 'Воронка завершённых с возражениями',
  'funnels.underage': 'Воронка несовершеннолетних',
  // Paths only 'create' records carry (the server logs a wider path list there).
  'appPage._id': 'Страница',
  platform: 'Платформа',
  department: 'Департамент',
  // The only path a 'clone' record carries.
  'rules.cloned': 'Скопировано правил',
};

const ACTION_LABELS: Record<string, string> = {
  create: 'Создание',
  edit: 'Редактирование',
  clone: 'Клонирование',
  enable: 'Включение',
  disable: 'Выключение',
  'enable-keywords': 'Включение кодовых слов',
  'disable-keywords': 'Выключение кодовых слов',
  'enable-refuses': 'Включение возражений',
  'disable-refuses': 'Выключение возражений',
};

const FUNNEL_KEYS: readonly string[] = [
  'manager',
  'completedNewDialog',
  'completedNewDialogWithRefuse',
  'underage',
];

type FunnelRefs = { _id: string }[] | null | undefined;
type FunnelsValue = Record<string, FunnelRefs> | null | undefined;

// Known keys first for a stable row order, then whatever else the snapshots
// carry — a funnel key added on the server later must still surface as a
// change instead of silently collapsing into the "composition unchanged" case.
const funnelKeysOf = (before: FunnelsValue, after: FunnelsValue): string[] => {
  const extra = [...Object.keys(before || {}), ...Object.keys(after || {})].filter(
    (key) => !FUNNEL_KEYS.includes(key)
  );
  return [...FUNNEL_KEYS, ...Array.from(new Set(extra))];
};

// A missing value on an existing page is recorded as null by the server, but
// for an optional flag that is a documented default rather than "no value":
// an absent config.isNightModeEnabled means night mode is on, an absent
// config.isRefusesEnabled means AI objections are off (see prophetTypes.Page).
// Without this the first toggle of either flag on a pre-existing page reads
// '— → выкл' instead of 'вкл → выкл'.
const ABSENT_DEFAULTS: Record<string, boolean> = {
  'config.isNightModeEnabled': true,
  'config.isRefusesEnabled': false,
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'вкл' : 'выкл';
  }
  if (typeof value === 'number' || typeof value === 'string') {
    return String(value);
  }
  return JSON.stringify(value);
};

// 'create' is excluded on purpose: there `before` is null because the page did
// not exist yet, so there is no prior state to substitute a default for — and
// PUT writes isRefusesEnabled: false explicitly, which would otherwise render
// as 'выкл → выкл'.
const formatChange = (action: string, path: string, value: unknown): string =>
  action !== 'create' && (value === null || value === undefined) && path in ABSENT_DEFAULTS
    ? formatValue(ABSENT_DEFAULTS[path])
    : formatValue(value);

export const PageAudit = ({ pageId, appPageId }: { pageId: string; appPageId?: string }) => {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useGetProphetPageAudit({ pageId, page, limit: PAGE_SIZE });
  // Funnel names for a readable diff; falls back to raw ids when unavailable.
  const { data: funnelsData } = useGetFunnels({ pageId: appPageId || '' });

  const funnelName = (id: string) =>
    funnelsData?.funnels.find((funnel) => funnel._id === id)?.name || id;
  const formatFunnels = (refs: FunnelRefs) =>
    refs?.length ? refs.map((ref) => funnelName(ref._id)).join(', ') : '—';

  if (isLoading) {
    return (
      <Center p="md">
        <Loader />
      </Center>
    );
  }

  // A failed request must not look like an empty history — the global React
  // Query config has retry: false, so a single failure would silently render
  // as "no changes yet".
  if (isError && !data) {
    return (
      <Text c="red" size="sm" p="md">
        Не удалось загрузить историю изменений. Обновите страницу или попробуйте позже.
      </Text>
    );
  }

  const records = data?.records || [];
  const totalPages = Math.ceil((data?.total || 0) / PAGE_SIZE);

  // The query refetches on every mount, so a failure can also hit while a
  // previous snapshot is cached (React Query keeps `data` on a failed refetch).
  // Showing it beats blanking the table, as long as it is labelled as stale.
  const staleWarning = isError ? (
    <Text c="yellow" size="sm" mb="xs">
      Не удалось обновить историю — показаны ранее загруженные данные.
    </Text>
  ) : null;

  if (!records.length) {
    return (
      <Box p="md">
        {staleWarning}
        <Text c="dimmed" size="sm">
          Изменений пока нет
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      {staleWarning}
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Когда</Table.Th>
            <Table.Th>Кто</Table.Th>
            <Table.Th>Действие</Table.Th>
            <Table.Th>Изменения</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {records.map((record) => (
            <Table.Tr key={record._id}>
              <Table.Td style={{ whiteSpace: 'nowrap' }}>
                {dayjs(record.at).format('DD.MM.YYYY HH:mm')}
              </Table.Td>
              <Table.Td>{record.userName || record.userId}</Table.Td>
              <Table.Td>{ACTION_LABELS[record.action] || record.action}</Table.Td>
              <Table.Td>
                {Object.entries(record.changes || {}).flatMap(([path, change]) => {
                  if (path === 'funnels') {
                    const before = (change.before || {}) as FunnelsValue;
                    const after = (change.after || {}) as FunnelsValue;
                    const changedKeys = funnelKeysOf(before, after).filter(
                      (key) =>
                        JSON.stringify(before?.[key] || []) !== JSON.stringify(after?.[key] || [])
                    );
                    if (changedKeys.length) {
                      return changedKeys.map((key) => (
                        <Text size="sm" key={`${path}.${key}`}>
                          {FIELD_LABELS[`funnels.${key}`] || `funnels.${key}`}:{' '}
                          {formatFunnels(before?.[key])} → {formatFunnels(after?.[key])}
                        </Text>
                      ));
                    }
                    // Every key normalized away (empty vs absent — e.g. an edit
                    // materializing optional funnels as []). The record is real,
                    // the composition is not, so say that instead of falling
                    // through to formatValue, which would dump both snapshots
                    // as raw JSON — the guaranteed rendering for the first edit
                    // of every page created before this feature.
                    return [
                      <Text size="sm" key={path}>
                        {FIELD_LABELS[path]}: состав не изменился
                      </Text>,
                    ];
                  }
                  if (path === 'rules.cloned') {
                    // after is an array of cloned rule ids — a count is the
                    // readable form, the ids themselves mean nothing here.
                    const clonedIds = Array.isArray(change.after) ? change.after : [];
                    return [
                      <Text size="sm" key={path}>
                        {FIELD_LABELS[path]}: {clonedIds.length}
                      </Text>,
                    ];
                  }
                  return [
                    <Text size="sm" key={path}>
                      {FIELD_LABELS[path] || path}:{' '}
                      {formatChange(record.action, path, change.before)} →{' '}
                      {formatChange(record.action, path, change.after)}
                    </Text>,
                  ];
                })}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {totalPages > 1 && <Pagination mt="md" value={page} onChange={setPage} total={totalPages} />}
    </Box>
  );
};
